"use client";

/**
 * CoachWaveform — live spectrum for the Listen/Whisper monitor.
 *
 * WHY CANVAS + rAF instead of React state (2026-07-25):
 * the old version animated 28 divs from ONE RMS scalar held in React state and
 * updated at frame rate. Two problems:
 *   1. Every bar moved together off a single number scaled by a static curve, so
 *      it read as a lump that "barely moves" rather than a voice.
 *   2. That state now lives in a provider wrapping the whole authenticated app,
 *      so 60 updates/sec re-rendered the entire tree.
 * This reads the analyser directly each frame and paints to a canvas: real
 * per-band motion, and React re-renders zero times while it animates.
 *
 * Visual: symmetric mirrored bars from the centre line, peak-hold caps that fall
 * slower than the signal, and a gradient tinted per mode (primary = listening,
 * blue = whispering).
 *
 *        peak caps ─┐
 *              ▁▂▄█ │ █▄▂▁
 *        ──────────────────  centre
 *              ▔▔▔▔   ▔▔▔▔
 */

import React, { useEffect, useRef } from "react";

interface Props {
  /** Pull the current spectrum; returns usable bin count (0 = no live graph). */
  readSpectrum: (out: Uint8Array<ArrayBuffer>) => number;
  /** Tints the gradient to match the session mode. */
  variant: "listen" | "whisper";
  className?: string;
}

const BARS = 40;
/** Voice lives low in the band; at 8kHz sampleRate Nyquist is 4kHz, and useful
 *  speech energy is ~300–3400Hz. Ignore the top bins so bars aren't dead air. */
const USABLE_FRACTION = 0.85;
/** Peak caps fall this fraction of remaining distance per frame (slow = punchy). */
const PEAK_FALL = 0.06;
/** Bar heights ease toward target so speech looks fluid, not strobed. */
const SMOOTH = 0.35;

export default function CoachWaveform({ readSpectrum, variant, className }: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const specRef = useRef<Uint8Array<ArrayBuffer>>(new Uint8Array(1024));
  const barsRef = useRef<Float32Array>(new Float32Array(BARS));
  const peaksRef = useRef<Float32Array>(new Float32Array(BARS));

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let width = 0;
    let height = 0;
    const dpr = Math.min(2, window.devicePixelRatio || 1);

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      width = Math.max(1, Math.floor(rect.width));
      height = Math.max(1, Math.floor(rect.height));
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    const hot = variant === "whisper"
      ? { top: "#2563eb", mid: "#60a5fa", peak: "#1d4ed8" }   // blue-600/400/700
      : { top: "#0ea5e9", mid: "#38bdf8", peak: "#0284c7" };  // sky-500/400/600

    const draw = () => {
      const binCount = readSpectrum(specRef.current);
      const bars = barsRef.current;
      const peaks = peaksRef.current;

      if (binCount > 0) {
        const usable = Math.max(8, Math.floor(binCount * USABLE_FRACTION));
        for (let b = 0; b < BARS; b++) {
          // Perceptual (roughly log) bucketing: low bands get fewer bins each so
          // voice fundamentals occupy more of the display than hiss does.
          const t0 = Math.pow(b / BARS, 1.6);
          const t1 = Math.pow((b + 1) / BARS, 1.6);
          const from = Math.floor(t0 * usable);
          const to = Math.max(from + 1, Math.floor(t1 * usable));
          let sum = 0;
          for (let i = from; i < to; i++) sum += specRef.current[i];
          const avg = sum / (to - from) / 255;
          // Gamma < 1 lifts quiet speech so normal talking clearly moves the bars
          // (the old linear mapping left conversation sitting near the floor).
          const target = Math.min(1, Math.pow(avg, 0.55) * 1.25);
          bars[b] += (target - bars[b]) * SMOOTH;
          peaks[b] = Math.max(bars[b], peaks[b] - (peaks[b] - bars[b]) * PEAK_FALL - 0.002);
        }
      } else {
        // No live graph — settle everything to rest instead of freezing mid-shape.
        for (let b = 0; b < BARS; b++) {
          bars[b] *= 0.88;
          peaks[b] *= 0.88;
        }
      }

      ctx.clearRect(0, 0, width, height);
      const mid = height / 2;
      const slot = width / BARS;
      const barW = Math.max(1.5, slot * 0.62);
      const maxH = mid - 2;

      const grad = ctx.createLinearGradient(0, 0, 0, height);
      grad.addColorStop(0, hot.top);
      grad.addColorStop(0.5, hot.mid);
      grad.addColorStop(1, hot.top);
      ctx.fillStyle = grad;

      for (let b = 0; b < BARS; b++) {
        const x = b * slot + (slot - barW) / 2;
        const h = Math.max(1, bars[b] * maxH);
        // Mirror around the centre line — reads as a waveform, not a bar chart.
        const r = Math.min(barW / 2, 2);
        ctx.beginPath();
        (ctx as any).roundRect?.(x, mid - h, barW, h * 2, r);
        if (!(ctx as any).roundRect) ctx.rect(x, mid - h, barW, h * 2);
        ctx.fill();
      }

      // Peak caps
      ctx.fillStyle = hot.peak;
      for (let b = 0; b < BARS; b++) {
        const x = b * slot + (slot - barW) / 2;
        const ph = Math.max(1, peaks[b] * maxH);
        if (ph < 2) continue;
        ctx.fillRect(x, mid - ph - 1, barW, 1.5);
        ctx.fillRect(x, mid + ph - 0.5, barW, 1.5);
      }

      // Idle centre line so the panel never looks broken during silence
      ctx.fillStyle = "rgba(100,116,139,0.25)"; // slate-500 @ 25%
      ctx.fillRect(0, mid - 0.5, width, 1);

      rafRef.current = requestAnimationFrame(draw);
    };

    rafRef.current = requestAnimationFrame(draw);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      ro.disconnect();
    };
  }, [readSpectrum, variant]);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      aria-hidden="true"
    />
  );
}
